import { useEffect, useRef, useState } from "react";
import securityVideo from "../assets/battery-dpp-video.mp4";
import { ShieldCheck, ClipboardCheck, Link2, RefreshCw, BatteryCharging } from "lucide-react";

const items = [
  {
    icon: ShieldCheck,
    title: "Blockchain-Based Data Integrity",
    desc: "Immutable distributed ledger technology guaranteeing permanent records and full transparency across the entire product lifecycle",
  },
  {
    icon: ClipboardCheck,
    title: "Complete Supply Chain Protection",
    desc: "Integrated security infrastructure delivering protected inventory management, secure distribution, and continuous operational visibility",
  },
  {
    icon: Link2,
    title: "Real-Time Authentication",
    desc: "Instant verification system enabling immediate product validation and counterfeit detection for guaranteed authenticity",
  },
  {
    icon: RefreshCw,
    title: "Seamless System Integration",
    desc: "Well-documented APIs for effortless integration with existing product management platforms.",
  },
];

function Security() {
  const [activeIndex,   setActiveIndex]   = useState(0);
  const spunRef       = useRef(new Set());
  const [spinningIndex, setSpinningIndex] = useState(null);
  const sectionRef    = useRef(null);
  const [visible,       setVisible]       = useState(false);
  const hasAnimated   = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setVisible(true);
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleMouseEnter = (i) => {
    setActiveIndex(i);
    if (!spunRef.current.has(i)) {
      spunRef.current.add(i);
      setSpinningIndex(i);
      setTimeout(() => setSpinningIndex(null), 600);
    }
  };

  return (
    <section ref={sectionRef} id="benefits" className="py-16 sm:py-20 lg:py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16 items-center">

        {/* ── LEFT: looping video panel ── */}
        <div
          style={{
            opacity:   visible ? 1 : 0,
            transform: visible ? "translateX(0)" : "translateX(-40px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          <div
            className="rounded-3xl shadow-xl w-[600px] h-[700px] max-w-full relative"
            style={{ overflow: "hidden" }}
          >
            <video
              src={securityVideo}
              autoPlay
              muted
              playsInline
              loop
              style={{
                position: "absolute", inset: 0,
                width: "100%", height: "100%",
                objectFit: "cover",
              }}
            />

            {/* Floating "Battery Passport" card — always visible now */}
            <div
              style={{
                position: "absolute",
                top: 20, left: 20,
                zIndex: 6,
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 16px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
              }}
            >
              <div style={{
                width: 30, height: 30, borderRadius: 9,
                background: "#2563eb",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <BatteryCharging size={16} color="#fff" />
              </div>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
                Battery Passport
              </span>
            </div>
          </div>
        </div>

        {/* ── RIGHT CONTENT ── */}
        <div
          style={{
            opacity:   visible ? 1 : 0,
            transform: visible ? "translateX(0)" : "translateX(40px)",
            transition: "opacity 0.7s ease 0.15s, transform 0.7s ease 0.15s",
          }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-5 leading-tight">
            Why choose Provinyx?
          </h2>

          <div className="space-y-3 mt-8">
            {items.map((item, i) => {
              const Icon       = item.icon;
              const isActive   = activeIndex   === i;
              const isSpinning = spinningIndex === i;

              return (
                <div
                  key={i}
                  onMouseEnter={() => handleMouseEnter(i)}
                  style={{
                    opacity:   visible ? 1 : 0,
                    transform: visible ? "translateY(0)" : "translateY(16px)",
                    transition: `opacity 0.5s ease ${0.3 + i * 0.08}s, transform 0.5s ease ${0.3 + i * 0.08}s`,
                  }}
                  className={`
                    flex items-start gap-4 cursor-pointer
                    px-4 py-4 rounded-2xl
                    transition-colors duration-300
                    ${isActive
                      ? "bg-blue-50 border border-blue-100"
                      : "bg-transparent border border-transparent hover:bg-blue-50/50"
                    }
                  `}
                >
                  <div
                    className={`
                      flex-shrink-0 w-12 h-12 rounded-xl
                      flex items-center justify-center
                      transition-all duration-300
                      ${isActive   ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-blue-100 text-blue-600"}
                      ${isSpinning ? "spin-once" : ""}
                    `}
                  >
                    <Icon size={22} />
                  </div>
                  <div className="pt-0.5">
                    <h4 className={`text-base sm:text-lg font-semibold transition-colors duration-200 ${isActive ? "text-blue-600" : "text-gray-900"}`}>
                      {item.title}
                    </h4>
                    <p className="text-gray-500 mt-1 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spinOnce {
          from { transform: rotate(0deg);   }
          to   { transform: rotate(360deg); }
        }
        .spin-once {
          animation: spinOnce 0.55s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }
      `}</style>
    </section>
  );
}

export default Security;